import { useMemo } from 'react'
import { useNavigate } from 'react-router'
import { Server, CheckCircle, AlertTriangle, Activity, Zap, Plus, HeartPulse, ArrowRight } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import pb from '@/lib/pb'
import { COLLECTIONS } from '@/types'
import type { TTenant, TJob, TAuditLog } from '@/types'
import { usePbSubscription } from '@/lib/usePbSubscription'
import { formatRelativeTime } from '@/lib/utils'
import { StatCard, EmptyState, Button } from '@/components/ui'

// ── Data hooks ─────────────────────────────────────────────────────────────

function useDashboardStats() {
  usePbSubscription(COLLECTIONS.TENANTS, ['dashboard-stats'])
  usePbSubscription(COLLECTIONS.HEALTH_CHECKS, ['dashboard-stats'])

  return useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: async () => {
      const [tenants, allJobs] = await Promise.all([
        pb.collection(COLLECTIONS.TENANTS).getFullList<TTenant>({ filter: 'is_deleted != true' }),
        pb.collection(COLLECTIONS.JOBS).getFullList<TJob>(),
      ])

      const healthy = tenants.filter(t => t.status === 'active').length
      const unhealthy = tenants.filter(t =>
        t.status === 'suspended' || t.status === 'provision_failed'
      ).length
      const activeJobCount = allJobs.filter(j => j.status === 'queued' || j.status === 'running').length

      return { total: tenants.length, healthy, unhealthy, activeJobs: activeJobCount }
    },
  })
}

function useLiveActivity() {
  usePbSubscription(COLLECTIONS.JOBS, ['live-activity'])
  usePbSubscription(COLLECTIONS.AUDIT_LOG, ['live-activity'])

  return useQuery({
    queryKey: ['live-activity'],
    queryFn: async () => {
      const [allJobs, recentAudit] = await Promise.all([
        pb.collection(COLLECTIONS.JOBS).getFullList<TJob>(),
        pb.collection(COLLECTIONS.AUDIT_LOG).getList<TAuditLog>(1, 10),
      ])
      const sortedJobs = allJobs.sort((a, b) => (b.id > a.id ? 1 : b.id < a.id ? -1 : 0))
      const sortedAudit = recentAudit.items.sort((a, b) => (b.id > a.id ? 1 : b.id < a.id ? -1 : 0))
      return {
        activeJobs: sortedJobs.filter(j => j.status === 'queued' || j.status === 'running').slice(0, 5),
        recentJobs: sortedJobs.filter(j => j.status === 'complete' || j.status === 'failed').slice(0, 5),
        auditItems: sortedAudit,
      }
    },
  })
}

// ── Feed item ──────────────────────────────────────────────────────────────

interface FeedItem {
  id: string
  label: string
  detail: string
  time: string
  statusColor: string
  sortKey: number
}

function jobColor(status: string): string {
  switch (status) {
    case 'running': return 'var(--status-provisioning)'
    case 'queued': return 'var(--status-warning)'
    case 'complete': return 'var(--status-healthy)'
    case 'failed': return 'var(--status-unhealthy)'
    default: return 'var(--text-tertiary)'
  }
}

// ── Dashboard Page ─────────────────────────────────────────────────────────

export default function DashboardPage() {
  const navigate = useNavigate()
  const { data: stats, isLoading: statsLoading, isError: statsError } = useDashboardStats()
  const { data: liveActivity, isError: liveError } = useLiveActivity()
  const hasError = statsError || liveError

  const feedItems = useMemo<FeedItem[]>(() => {
    const items: FeedItem[] = []

    if (liveActivity?.activeJobs) {
      for (const job of liveActivity.activeJobs) {
        items.push({
          id: `job-active-${job.id}`,
          label: job.title,
          detail: job.job_type,
          time: job.created,
          statusColor: jobColor(job.status),
          sortKey: new Date(job.created).getTime(),
        })
      }
    }

    if (liveActivity?.recentJobs) {
      for (const job of liveActivity.recentJobs) {
        items.push({
          id: `job-recent-${job.id}`,
          label: job.title,
          detail: job.job_type,
          time: job.updated,
          statusColor: jobColor(job.status),
          sortKey: new Date(job.updated).getTime(),
        })
      }
    }

    if (liveActivity?.auditItems) {
      for (const entry of liveActivity.auditItems) {
        items.push({
          id: `audit-${entry.id}`,
          label: entry.event,
          detail: entry.actor,
          time: entry.timestamp || entry.created,
          statusColor: 'var(--brand)',
          sortKey: new Date(entry.timestamp || entry.created || 0).getTime(),
        })
      }
    }

    items.sort((a, b) => b.sortKey - a.sortKey)
    return items.slice(0, 15)
  }, [liveActivity])

  const hasActivity = feedItems.length > 0

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div style={{ flex: 1, overflow: 'auto', padding: '24px' }}>

        {/* Error banner */}
        {hasError && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '12px 16px',
            marginBottom: 16,
            backgroundColor: 'var(--status-unhealthy-dim)',
            border: '1px solid var(--status-unhealthy)',
            borderRadius: 'var(--radius-md)',
            fontSize: 13,
            color: 'var(--status-unhealthy)',
          }}>
            <AlertTriangle size={16} />
            Failed to load data -- check PB auth and collection rules.
          </div>
        )}

        {/* Stat cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: '16px',
          marginBottom: '24px',
        }}>
          <StatCard
            icon={<Server size={20} />}
            label="Total Tenants"
            value={stats?.total ?? 0}
            color="var(--text-primary)"
            isLoading={statsLoading}
          />
          <StatCard
            icon={<CheckCircle size={20} />}
            label="Healthy"
            value={stats?.healthy ?? 0}
            color="var(--status-healthy)"
            isLoading={statsLoading}
          />
          <StatCard
            icon={<AlertTriangle size={20} />}
            label="Unhealthy"
            value={stats?.unhealthy ?? 0}
            color="var(--status-unhealthy)"
            isLoading={statsLoading}
            alert={(stats?.unhealthy ?? 0) > 0}
            alertColor="var(--status-unhealthy)"
          />
          <StatCard
            icon={<Activity size={20} />}
            label="Active Jobs"
            value={stats?.activeJobs ?? 0}
            color="var(--status-provisioning)"
            isLoading={statsLoading}
            alert={(stats?.activeJobs ?? 0) > 0}
            alertColor="var(--status-provisioning)"
          />
        </div>

        {/* Two-column content */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '3fr 2fr',
          gap: '20px',
        }}>

          {/* Live Activity Feed */}
          <div style={{
            backgroundColor: 'var(--bg-secondary)',
            border: '1px solid var(--border-subtle)',
            borderRadius: 'var(--radius-lg)',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}>
            <div style={{
              padding: '16px 20px',
              borderBottom: '1px solid var(--border-subtle)',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}>
              <Zap size={16} style={{ color: 'var(--brand)' }} />
              <span style={{
                fontSize: '14px',
                fontWeight: 600,
                color: 'var(--text-primary)',
              }}>
                Live Activity
              </span>
            </div>

            {!hasActivity ? (
              <EmptyState
                icon={<Zap size={32} />}
                title="No activity yet"
                description="Provision your first tenant to get started"
              />
            ) : (
              <div style={{ flex: 1, overflowY: 'auto' }}>
                {feedItems.map((item, index) => (
                  <div
                    key={item.id}
                    className="fade-slide-up"
                    style={{
                      padding: '12px 20px',
                      borderBottom: '1px solid var(--border-subtle)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      animationDelay: `${index * 30}ms`,
                    }}
                  >
                    <span style={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      backgroundColor: item.statusColor,
                      flexShrink: 0,
                    }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontSize: '13px',
                        fontWeight: 500,
                        color: 'var(--text-primary)',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}>
                        {item.label}
                      </div>
                      <div style={{
                        fontSize: '12px',
                        color: 'var(--text-secondary)',
                        marginTop: '2px',
                      }}>
                        {item.detail}
                      </div>
                    </div>
                    <span style={{
                      fontSize: '11px',
                      color: 'var(--text-tertiary)',
                      whiteSpace: 'nowrap',
                      fontFamily: 'var(--font-mono)',
                    }}>
                      {formatRelativeTime(item.time)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Quick Actions */}
          <div style={{
            backgroundColor: 'var(--bg-secondary)',
            border: '1px solid var(--border-subtle)',
            borderRadius: 'var(--radius-lg)',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}>
            <div style={{
              padding: '16px 20px',
              borderBottom: '1px solid var(--border-subtle)',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}>
              <Activity size={16} style={{ color: 'var(--brand)' }} />
              <span style={{
                fontSize: '14px',
                fontWeight: 600,
                color: 'var(--text-primary)',
              }}>
                Quick Actions
              </span>
            </div>

            <div style={{
              padding: '20px',
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
            }}>
              <Button
                variant="primary"
                size="lg"
                icon={<Plus size={16} />}
                onClick={() => navigate('/tenants')}
                style={{ width: '100%' }}
              >
                New Tenant
              </Button>
              <Button
                variant="secondary"
                size="lg"
                icon={<HeartPulse size={16} />}
                style={{ width: '100%' }}
              >
                Run Health Check
              </Button>
              <Button
                variant="ghost"
                size="lg"
                icon={<ArrowRight size={16} />}
                onClick={() => navigate('/jobs')}
                style={{ width: '100%' }}
              >
                View All Jobs
              </Button>
            </div>
          </div>

        </div>
      </div>
    </div>
  )
}
